import { useCallback, useEffect, useState } from "react";
import {
  APP_BUILD_ID,
  DISMISSED_APP_BUILD_STORAGE_KEY,
} from "@/appVersion";
import { useAppVersion } from "@/context/AppVersionProvider";
import "./app-version-notifier.css";

interface AppVersionNotifierProps {
  currentBuildId?: string;
  reload?: () => void;
  storage?: Pick<Storage, "getItem" | "setItem">;
}

export function AppVersionNotifier({
  currentBuildId = APP_BUILD_ID,
  reload = () => window.location.reload(),
  storage = window.localStorage,
}: AppVersionNotifierProps = {}) {
  const { deployedManifest, disabled } = useAppVersion();
  const [availableBuildId, setAvailableBuildId] = useState<string>();

  const dismissedBuildId = useCallback(() => {
    try {
      return storage.getItem(DISMISSED_APP_BUILD_STORAGE_KEY);
    } catch {
      return null;
    }
  }, [storage]);

  useEffect(() => {
    if (disabled) {
      setAvailableBuildId(undefined);
      return;
    }
    const deployedBuildId = deployedManifest?.buildId;
    if (!deployedBuildId || deployedBuildId === currentBuildId || dismissedBuildId() === deployedBuildId) {
      setAvailableBuildId(undefined);
    } else {
      setAvailableBuildId(deployedBuildId);
    }
  }, [currentBuildId, deployedManifest, disabled, dismissedBuildId]);

  if (!availableBuildId || disabled) return null;

  const dismiss = () => {
    try {
      storage.setItem(DISMISSED_APP_BUILD_STORAGE_KEY, availableBuildId);
    } catch {
      // Dismiss for this mounted session even if browser storage is unavailable.
    }
    setAvailableBuildId(undefined);
  };

  return (
    <aside className="app-version-notice" role="status" aria-live="polite">
      <span className="app-version-notice-text">New app version available</span>
      <button className="app-version-reload" type="button" onClick={reload}>
        Reload
      </button>
      <button
        className="app-version-dismiss"
        type="button"
        aria-label="Dismiss new version notification"
        onClick={dismiss}
      >
        ×
      </button>
    </aside>
  );
}
